import { memo, useCallback, useMemo } from 'react';
import { AlertBanner, Badge, Switch, TextArea } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { LayoutGrid, Mail, Shield, UserCog, Users } from 'lucide-react';

import { USER_TYPE, USER_TYPE_META } from '../../constants/copy.js';
import { INVITE_EXPIRY_DAYS, INVITE_STEP } from '../../constants/inviteRules.js';
import { useInviteMember } from '../../hooks/useInviteMember';
import StudioBadge from '../shared/StudioBadge';
import ReviewSection from './ReviewSection';

/**
 * Review step: a summary per section with Edit, the Send invitation email toggle and the
 * optional personal note.
 *
 * @param {object} props
 * @param {Array} props.roles Roles as mapped by `useInviteOptions`.
 * @param {Array<{ id: string, name: string }>} props.groups
 * @param {Array<{ id: string, name: string }>} [props.studios] Every studio (shown for an Admin).
 * @param {boolean} [props.hasError] The last submit failed.
 */
function ReviewStep({ roles, groups, studios: allStudios = [], hasError = false }) {
  const { t } = useTranslation('people-access');
  const { state, recipientCount, goToStep, setSendEmail, setNote } = useInviteMember();
  const isMember = state.userType === USER_TYPE.MEMBER;
  const typeMeta = USER_TYPE_META[state.userType];

  const selectedRoles = useMemo(
    () => roles.filter((r) => state.roleIds.includes(r.id)),
    [roles, state.roleIds],
  );
  const studios = useMemo(
    () => [...new Map(selectedRoles.flatMap((r) => r.studios).map((s) => [s.id, s])).values()],
    [selectedRoles],
  );
  const selectedGroups = useMemo(
    () => groups.filter((g) => state.groupIds.includes(g.id)),
    [groups, state.groupIds],
  );

  const handleNote = useCallback((event) => setNote(event.target.value), [setNote]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="m-0 text-sm font-semibold text-content">{t('invite.review.label')}</h2>
        <p className="m-0 text-xs text-content-muted">{t('invite.review.helper')}</p>
      </div>

      {hasError && (
        <AlertBanner tone="danger" title={t('invite.review.errorTitle')}>
          {t('invite.review.errorBody')}
        </AlertBanner>
      )}

      <ReviewSection
        icon={Mail}
        title={t('invite.review.recipients', { count: recipientCount })}
        stepId={INVITE_STEP.PEOPLE}
        onEdit={goToStep}
      >
        {state.emails.map((email) => (
          <Badge key={email} variant="secondary">
            {email}
          </Badge>
        ))}
      </ReviewSection>

      <ReviewSection
        icon={UserCog}
        title={t('invite.review.userType')}
        stepId={INVITE_STEP.PEOPLE}
        onEdit={goToStep}
      >
        <Badge variant={typeMeta.variant}>{t(typeMeta.labelKey)}</Badge>
        {!isMember && <span>{t('invite.review.adminDescription')}</span>}
      </ReviewSection>

      {isMember && (
        <ReviewSection
          icon={Shield}
          title={t('invite.review.roles')}
          stepId={INVITE_STEP.ROLES}
          onEdit={goToStep}
        >
          {selectedRoles.length === 0 ? (
            <span>{t('invite.review.none')}</span>
          ) : (
            selectedRoles.map((role) => (
              <Badge key={role.id} variant="secondary">
                {`${role.name} · ${t('invite.review.permissions', { count: role.permissionCount })}`}
              </Badge>
            ))
          )}
        </ReviewSection>
      )}

      <ReviewSection
        icon={LayoutGrid}
        tone="Success"
        title={t(isMember ? 'invite.review.studioAccess' : 'invite.review.studioAccessAdmin')}
        stepId={isMember ? INVITE_STEP.ROLES : undefined}
        onEdit={goToStep}
      >
        {!isMember ? (
          <>
            {allStudios.map((studio) => (
              <StudioBadge key={studio.id} id={studio.id} name={studio.name} />
            ))}
            <Badge variant="informative" size="small">
              {t('invite.review.adminConsole')}
            </Badge>
          </>
        ) : studios.length === 0 ? (
          <span>{t('invite.review.none')}</span>
        ) : (
          studios.map((studio) => (
            <StudioBadge key={studio.id} id={studio.id} name={studio.name} />
          ))
        )}
      </ReviewSection>

      {isMember && (
        <ReviewSection
          icon={Users}
          tone="Purple"
          title={t('invite.groups.label')}
          stepId={INVITE_STEP.GROUPS}
          onEdit={goToStep}
        >
          {selectedGroups.length === 0 ? (
            <span>{t('invite.review.none')}</span>
          ) : (
            selectedGroups.map((group) => (
              <Badge key={group.id} variant="secondary">
                {group.name}
              </Badge>
            ))
          )}
        </ReviewSection>
      )}

      <div className="rounded-lg border border-edge bg-surface p-4">
        <Switch
          checked={state.sendEmail}
          onChange={setSendEmail}
          label={t('invite.review.sendLabel')}
          description={
            state.sendEmail
              ? t('invite.review.sendOn', {
                  count: recipientCount,
                  email: state.emails[0],
                  days: INVITE_EXPIRY_DAYS,
                })
              : t('invite.review.sendOff')
          }
        />
      </div>

      {state.sendEmail && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invite-personal-note" className="text-sm font-medium text-content-secondary">
            {t('invite.review.noteLabel')}{' '}
            <span className="font-normal text-content-muted">{t('invite.people.optional')}</span>
          </label>
          <TextArea
            id="invite-personal-note"
            rows={4}
            value={state.note}
            onChange={handleNote}
            placeholder={t('invite.review.notePlaceholder')}
          />
        </div>
      )}
    </div>
  );
}

export default memo(ReviewStep);
